/* v3.18-1289 — Reusable Client Local Time
 *
 * ONE component to display a client's live-updating local time, timezone
 * abbreviation, and color-coded quiet-hours status.  Consumed by:
 *   • Ghost AI Call Dialog       (first implementation)
 *   • Client Dashboard header
 *   • CRM contact panel
 *   • Appointment Scheduler
 *   • Dialer
 *   • SMS composer
 *   • Compose & Approve compose-time warning
 *
 * Time thresholds (client-local):
 *   • Green  — before 20:00
 *   • Yellow — 20:00–20:59        (quiet hours begin at 21:00)
 *   • Red    — 21:00 and later    (currently after 9pm)
 *
 * The status theming is EXPOSED to the parent so a dialog can adopt a
 * red warning theme while the operator remains in control (Call Now is
 * NEVER disabled — per operator directive).
 *
 * Props:
 *   • clientId  string   — resolves timezone via /api/util/client-local-time
 *   • timezone  string   — bypass server lookup with an explicit IANA zone
 *   • variant   string   — "chip" (compact) | "banner" (full dialog banner)
 *   • onStatus  fn       — invoked with { status, tz, guessed, hh, mm }
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { AlertTriangle, Clock, MoonStar } from 'lucide-react';

const STATUS = {
  green:  { label: 'Fine to call',              color: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200', dot: 'bg-emerald-500' },
  yellow: { label: 'Approaching quiet hours',   color: 'text-amber-800',   bg: 'bg-amber-50',    border: 'border-amber-300',   dot: 'bg-amber-500'   },
  red:    { label: 'After client quiet hours',  color: 'text-red-800',     bg: 'bg-red-50',      border: 'border-red-300',     dot: 'bg-red-500'     },
};

function statusForHour(hour24) {
  if (hour24 >= 21) return 'red';
  if (hour24 >= 20) return 'yellow';
  return 'green';
}

/* Format the client's local time using Intl (browser-native — no
 * moment/luxon dependency). Returns { pretty, tzAbbr, hour24, minute }
 * or null on invalid tz. */
function nowInZone(tz) {
  try {
    const now = new Date();
    // Parts we need
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour:   'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).formatToParts(now);
    const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    const pretty = `${map.hour}:${map.minute} ${map.dayPeriod || ''} ${map.timeZoneName || ''}`.trim();

    // Hour in 24h to drive the status band.
    const h24Parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: '2-digit', hour12: false,
    }).formatToParts(now);
    const hour24 = parseInt(h24Parts.find((p) => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(map.minute || '0', 10);
    return { pretty, tzAbbr: map.timeZoneName || tz, hour24, minute };
  } catch (_e) {
    return null;
  }
}

export default function ClientLocalTime({
  clientId,
  timezone: tzProp,
  variant = 'chip',
  onStatus,
  className = '',
}) {
  const [resolved, setResolved] = useState(
    tzProp ? { timezone: tzProp, guessed: false, source: 'prop' } : null,
  );
  const [tick, setTick]         = useState(0);
  const [error, setError]       = useState(null);

  /* Resolve timezone from clientId if not passed explicitly */
  useEffect(() => {
    if (tzProp) { setResolved({ timezone: tzProp, guessed: false, source: 'prop' }); return; }
    if (!clientId) { return; }
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get(`/util/client-local-time/${clientId}`);
        if (!dead) setResolved(data);
      } catch (e) {
        if (!dead) setError(e?.response?.data?.detail || 'Unable to resolve client timezone');
      }
    })();
    return () => { dead = true; };
  }, [clientId, tzProp]);

  /* Ticker — refresh once per 15 seconds; sufficient for a minute-
   * granular clock without churn. */
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const info = useMemo(() => {
    if (!resolved?.timezone) return null;
    return nowInZone(resolved.timezone);
    // `tick` intentionally invalidates memo every 15s so the clock
    // advances even though nowInZone doesn't reference it.

  }, [resolved?.timezone, tick]);

  const status = info ? statusForHour(info.hour24) : 'green';

  useEffect(() => {
    if (!info) return;
    onStatus?.({
      status,
      timezone: resolved?.timezone,
      tzAbbr:   info.tzAbbr,
      hour24:   info.hour24,
      minute:   info.minute,
      guessed:  !!resolved?.guessed,
    });
    // ``info`` is derived from resolved.timezone/tick and reported to
    // onStatus indirectly via ``status``; adding it as a dep would
    // trigger onStatus every 15s on every re-render.

  }, [status, resolved?.timezone, resolved?.guessed, onStatus]);

  if (error) {
    return (
      <span className={`text-xs text-slate-500 ${className}`} data-testid="client-local-time-error">
        Time unavailable
      </span>
    );
  }
  if (!info) {
    return (
      <span className={`text-xs text-slate-400 ${className}`} data-testid="client-local-time-loading">
        Loading local time…
      </span>
    );
  }

  const theme = STATUS[status];

  if (variant === 'banner') {
    return (
      <div
        className={`w-full rounded-md border px-3 py-2 flex items-start gap-2 ${theme.bg} ${theme.border} ${theme.color} ${className}`}
        data-testid={`client-local-time-banner-${status}`}
        role="status"
      >
        <span className={`mt-1 h-2.5 w-2.5 rounded-full ${theme.dot}`} aria-hidden />
        <div className="text-sm leading-tight flex-1">
          <div className="font-semibold flex items-center gap-2">
            {status === 'red'  ? <MoonStar size={14} /> : (status === 'yellow' ? <AlertTriangle size={14} /> : <Clock size={14} />)}
            <span data-testid="client-local-time-pretty">{info.pretty}</span>
            <span className="text-xs opacity-70">in client&apos;s local timezone</span>
          </div>
          <div className="text-xs mt-1">
            {status === 'yellow' && 'Client quiet hours begin at 9:00 PM local time.'}
            {status === 'red'    && "It is currently after 9:00 PM in the client\u2019s local timezone."}
            {status === 'green'  && `${theme.label} · ${resolved?.timezone}${resolved?.guessed ? ' (inferred)' : ''}`}
          </div>
        </div>
      </div>
    );
  }

  // "chip" — compact inline
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${theme.bg} ${theme.border} ${theme.color} ${className}`}
      data-testid={`client-local-time-chip-${status}`}
      title={`${resolved?.timezone}${resolved?.guessed ? ' (inferred)' : ''}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${theme.dot}`} aria-hidden />
      <Clock size={11} />
      <span data-testid="client-local-time-pretty">{info.pretty}</span>
    </span>
  );
}
